/* eslint-disable react/forbid-prop-types */
/* eslint-disable react/no-unused-prop-types */
import { useEffect, useMemo, useRef } from 'react';
import { bool, func, node, object, oneOfType, shape, string } from 'prop-types';
import _, { findIndex } from 'lodash';
import clsx from 'clsx';
import { Accordion, AccordionSummary, List, ListItemIcon, ListItemText } from '@material-ui/core';
import { ArrowForward as RightArrowIcon, Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@material-ui/icons';
import { t } from '@lingui/macro';

import { useFilterState } from '../../../stores/FilterStateStore';
import messageDescriptor from '../../lingui/LinguiPropTypes';
import MenuActions, { actionsType } from './MenuActions';
import MenuGroupItem from './MenuGroupItem';
import useStyles from './MenuStyles';
import Link from '../../links/Link';
import { linkType, useIsActive, useIsLinkActive, useUrl } from './LinkUtilities';
import { useHistory, useIsExpanded } from './MenuUtilities';
import { useAllowDrillIn, useUser } from '../../../stores/SessionStore';
import { useNavigate } from '../../../utilities/PgmUtilities';

export default function MenuGroupHistoryItem({ id, icon, title, link, historyConfig, actions, onActiveChange }) {
    const classes = useStyles();
    const [user] = useUser();
    const [history, { addHistoryItem, removeHistoryItem, resetHistory }] = useHistory(user, id, historyConfig);
    const historyHasBeenReset = useRef(false);
    const [expanded, setExpanded] = useIsExpanded(user, id, history.length > 0);
    const [filterState] = useFilterState();
    const navigate = useNavigate();
    const url = useUrl(link);
    const [canDrillIn] = useAllowDrillIn();

    const isActive = useIsActive(link);
    const isLinkActive = useIsLinkActive();
    const historyItemActive = useMemo(
        () => findIndex(history, (item) => isLinkActive(item.link)) !== -1,
        [history, isLinkActive]
    );

    useEffect(() => {
        if (historyConfig.shouldResetHistory && !historyHasBeenReset.current) {
            resetHistory();
            historyHasBeenReset.current = true;
        }
    }, [historyConfig, resetHistory]);

    useEffect(() => {
        if (!filterState) return;

        if (isLinkActive(historyConfig.link)) {
            const filterStateSummary = _.pick(filterState.summarized, historyConfig.filterId);
            const encodedFilterState = _.pick(filterState.encoded, historyConfig.filterId);

            if (_.isEmpty(filterStateSummary)) return;

            addHistoryItem({
                title: filterStateSummary[historyConfig.filterId].value,
                link: {
                    ...historyConfig.link,
                    encodedFilterState,
                },
            });
        }
    }, [addHistoryItem, filterState, historyConfig.filterId, historyConfig.link, isLinkActive]);

    const onActiveChangeRef = useRef();
    onActiveChangeRef.current = onActiveChange;
    const linkRef = useRef();
    linkRef.current = link;

    useEffect(() => {
        onActiveChangeRef.current(linkRef.current, isActive || historyItemActive);
        setExpanded(expanded || historyItemActive);
    }, [isActive, historyItemActive, setExpanded, expanded]);

    useEffect(() => {
        if (history.length === 0 && expanded) setExpanded(false);
    }, [expanded, history.length, setExpanded]);

    function onRemoveHistoryItem(item) {
        removeHistoryItem(item);

        if (isLinkActive(item.link)) navigate(url);
    }

    function handleLinkClick(event) {
        // Don't allow link clicks to change the accordion expansion state.
        event.stopPropagation();
    }

    return (
        <Accordion
            className={classes.historyGroup}
            expanded={expanded}
            onChange={(_event, state) => setExpanded(state)}
            elevation={0}
        >
            <AccordionSummary
                id={`side-nav-item-${id}`}
                className={clsx({ 'Mui-selected': isActive })}
                expandIcon={canDrillIn && history.length > 0 && <ExpandMoreIcon />}
            >
                <Link to={url} state={{ navFromSidebar: true }} onClick={handleLinkClick}>
                    <ListItemIcon>{icon}</ListItemIcon>
                    <ListItemText>{t`${title}`}</ListItemText>
                </Link>
                <MenuActions actions={actions} />
            </AccordionSummary>
            {canDrillIn && (
                <List>
                    {history.map((item, index) => (
                        <MenuGroupItem
                            id={`${id}-history-${index}`}
                            key={item.link.encodedFilterState[historyConfig.filterId]}
                            className={classes.historyItem}
                            title={item.title}
                            icon={<RightArrowIcon />}
                            link={item.link}
                            actions={[
                                {
                                    id: 'remove-history-item',
                                    icon: <CloseIcon fontSize="small" />,
                                    tooltip: t`Remove this history item`,
                                    onClick: () => onRemoveHistoryItem(item),
                                },
                            ]}
                            onActiveChange={historyConfig.onActiveChange || _.noop}
                        />
                    ))}
                </List>
            )}
        </Accordion>
    );
}

MenuGroupHistoryItem.propTypes = {
    id: string.isRequired,
    title: oneOfType([string, messageDescriptor]).isRequired,
    icon: node.isRequired,
    link: linkType.isRequired,
    historyConfig: shape({
        filterId: string.isRequired,
        link: linkType.isRequired,
        onActiveChange: func,
        shouldResetHistory: bool.isRequired,
    }).isRequired,
    actions: actionsType,
    dragItem: object,
    onActiveChange: func,
};

MenuGroupHistoryItem.defaultProps = {
    actions: undefined,
    dragItem: undefined,
    onActiveChange: _.noop,
};
